import { Flex, HStack, Stack, Text } from '@chakra-ui/react';
import { Navigate, useNavigate } from 'react-router-dom';

import DarkModal from '../../../../shared/overlay/DarkModal';
import ExpertCountLimit from '../ExpertCountLimit';
import ExpertsTable from '../experts-overview/ExpertsTable';
import Loading from '@/client/components/media/Loading';
import NoExpertsOverviewPlaceholder from '../experts-overview/NoExpertsOverviewPlaceholder';
import OutlineButton from '@/client/components/shared/buttons/OutlineButton';
import { Plus } from '@/client/components/icons/ContinuIcons';
import { useCheckFeatureFlag } from '@/client/services/hooks/auth/useCheckFeatureFlag';
import { useExpertsStore } from '../expertsStore';
import { useGetPublicImage } from '@/client/services/hooks/media/useGetPublicImage';
import { useListExperts } from '../hooks/useListExperts';
import { useTranslation } from 'react-i18next';

export default function ExpertsOverview() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { setIsEditingExpert } = useExpertsStore();
  const { getPublicImage } = useGetPublicImage();
  const eddyEnabled = useCheckFeatureFlag('enable_knowledgebase_ai_eddy_assistant');
  const expertsEnabled = useCheckFeatureFlag('enable_ai_eddy_experts');

  if (!eddyEnabled) return <Navigate to="/admin/eddy" replace />;

  const { data, isLoading, isError } = useListExperts({ includeTotalCount: true, pageSize: 1 });

  if (isLoading) return <Loading />;

  if (isError) return <div>{t('admin.aiAgent.experts.overview.error')}</div>;

  const { limitInfo, pageInfo } = data;
  const { totalCount } = pageInfo;
  const { totalCurrentCount, limit } = limitInfo;

  const limitReached = totalCurrentCount === limit;

  return (
    <Stack spacing={16} padding={10} paddingTop={6} height="full" overflowY="auto">
      <Flex justifyContent="space-between" alignItems="center">
        <Stack spacing={2}>
          <Text fontSize="22px" fontWeight={600} color="baseBlack">
            {t('admin.aiAgent.experts.overview.title')}
          </Text>

          <Text fontSize="14px" fontWeight={500} color="neutral.800">
            {t('admin.aiAgent.experts.overview.description')}
          </Text>
        </Stack>

        <HStack spacing={4}>
          <ExpertCountLimit
            totalExperts={totalCurrentCount}
            expertsLimit={limit}
            limitReached={limitReached}
          />

          <OutlineButton
            isDisabled={limitReached}
            minWidth="fit-content"
            width="fit-content"
            leftIcon={<Plus />}
            onClick={() => {
              setIsEditingExpert(true);
              navigate('/admin/eddy/experts/details');
            }}
          >
            {t('admin.aiAgent.experts.overview.createExpert')}
          </OutlineButton>
        </HStack>
      </Flex>

      {totalCount === 0 && <NoExpertsOverviewPlaceholder />}

      {totalCount && totalCount > 0 && <ExpertsTable />}

      <DarkModal
        isOpen={!expertsEnabled}
        onClose={() => {}}
        imageSrc={getPublicImage('continu/app-images/eddy-modal-2.jpg')}
        title={t('admin.aiAgent.experts.overview.tryExperts.title')}
        description={t('admin.aiAgent.experts.overview.tryExperts.helpText')}
        submitButton={{
          label: t('admin.aiAgent.experts.overview.tryExperts.buttonCopy'),
          onClick: () => window.open('https://form.typeform.com/to/lX05D52y', '_blank'),
        }}
        cancelButton={{
          label: t('admin.aiAgent.experts.overview.returnToEddyDashboard'),
          onClick: () => navigate('/admin/eddy'),
        }}
      />
    </Stack>
  );
}
